import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { fiscalSituationDigits } from '@/modules/accounting/accountingDocumentMovement'
import { getAccountingProductMemoryDetails } from './accountingFiscalMemoryService'
import type {
  AccountingProduct,
  FiscalMemoryOperationEvidence,
  FiscalMemoryProductDetails,
  FiscalMemoryTaxEvidence,
} from './accountingFiscalMemoryTypes'

interface Props {
  product: AccountingProduct | null
  isOpen: boolean
  onClose: () => void
}

type DetailsTab = 'summary' | 'operations' | 'taxes' | 'taxReform'

const CURRENT_TAX_ORDER = ['ipi', 'icms', 'pis', 'cofins'] as const
const TAX_REFORM_ORDER = ['ibs', 'cbs'] as const

interface OperationTaxGroup {
  key: string
  reference: FiscalMemoryTaxEvidence
  occurrenceCount: number
  totalOccurrences: number
  confidence: number
}

interface MemoryOperationGroup {
  key: string
  direction: 'incoming' | 'outgoing'
  operation: FiscalMemoryOperationEvidence | null
  taxes: OperationTaxGroup[]
}

const emptyDetails: FiscalMemoryProductDetails = {
  party_codes: [],
  operation_evidence: [],
  tax_evidence: [],
}

export function FiscalMemoryDetailsModal({ product, isOpen, onClose }: Props) {
  const { t, locale } = useTranslation()
  const [activeTab, setActiveTab] = useState<DetailsTab>('summary')
  const [details, setDetails] = useState<FiscalMemoryProductDetails>(emptyDetails)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !product) return
    let cancelled = false
    setActiveTab('summary')
    setDetails(emptyDetails)
    setError(null)
    setLoading(true)
    getAccountingProductMemoryDetails(product.unit_id, product.asset_id)
      .then((loaded) => { if (!cancelled) setDetails(loaded) })
      .catch((loadError) => {
        console.error('[FiscalMemoryDetailsModal] Load error:', loadError)
        if (!cancelled) setError(t('accounting.fiscalMemory.details.errors.load'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, product, t])

  const formatDate = (value: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(`${value.slice(0, 10)}T12:00:00`))
    : '—'
  const formatPercent = (value: number | null) => value === null
    ? '—'
    : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value)}%`
  const translatedOrValue = (key: string, value: string | null) => {
    if (!value) return '—'
    const translated = t(key)
    return translated === key ? value : translated
  }
  const purposeLabel = (value: string | null) => {
    const normalized = value || 'unknown'
    return translatedOrValue(`accounting.fiscalMemory.itemTypes.${normalized}`, normalized)
  }
  const sourceLabel = (value: string) => translatedOrValue(
    `accounting.fiscalMemory.sources.${value}`,
    value,
  )
  const confidenceClass = product?.classification_status === 'strong'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : product?.classification_status === 'review'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'

  const operationGroups = useMemo(() => {
    const grouped = new Map<string, {
      operation: FiscalMemoryOperationEvidence | null
      taxes: FiscalMemoryTaxEvidence[]
    }>()
    details.operation_evidence.forEach((operation) => {
      grouped.set(operation.id, { operation, taxes: [] })
    })
    details.tax_evidence.forEach((tax) => {
      const key = tax.operation_memory_id || tax.source_item_key
      const operation = details.operation_evidence.find((candidate) => candidate.id === key)
      const current = grouped.get(key) || { operation: null, taxes: [] }
      grouped.set(key, { operation: current.operation || operation || null, taxes: [...current.taxes, tax] })
    })
    return Array.from(grouped.entries()).map(([key, value]): MemoryOperationGroup => {
      const taxGroups = new Map<string, FiscalMemoryTaxEvidence[]>()
      value.taxes.forEach((tax) => {
        const taxKey = tax.tax_code.toLowerCase()
        taxGroups.set(taxKey, [...(taxGroups.get(taxKey) || []), tax])
      })
      const taxReference = value.taxes[0]
      const operationDirection = String(value.operation?.attrs?.movement_direction || '')
      const direction = operationDirection === 'outgoing'
        || value.operation?.operation_type_key === 'outgoing_sale'
        || taxReference?.movement_direction === 'outgoing'
        ? 'outgoing' as const
        : 'incoming' as const
      return {
        key,
        direction,
        operation: value.operation,
        taxes: Array.from(taxGroups.entries()).map(([taxKey, evidence]) => {
          const preferred = evidence.find((item) => item.evidence_role === 'prior_bookkeeping') || evidence[0]
          return {
            key: `${key}|${taxKey}`,
            reference: preferred,
            occurrenceCount: Math.round(Number(preferred.occurrence_count || 0) * Number(preferred.confidence || 0)),
            totalOccurrences: Number(preferred.occurrence_count || 0),
            confidence: Math.round(Number(preferred.confidence || 0) * 100),
          }
        }),
      }
    }).sort((left, right) => {
      const leftDate = left.operation?.last_seen_at || left.taxes[0]?.reference.last_seen_at || ''
      const rightDate = right.operation?.last_seen_at || right.taxes[0]?.reference.last_seen_at || ''
      return rightDate.localeCompare(leftDate)
    })
  }, [details.operation_evidence, details.tax_evidence])

  const feedback = loading ? (
    <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
      {t('accounting.fiscalMemory.details.loading')}
    </p>
  ) : error ? (
    <p className="rounded-xl bg-rose-50 p-5 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>
  ) : null

  const taxValues = (evidence: FiscalMemoryTaxEvidence) => (
    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-200 pt-3 text-xs dark:border-slate-700">
      {Boolean(evidence.attrs?.classification_code) && <div className="col-span-2"><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.classification')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{String(evidence.attrs.classification_code)}</dd></div>}
      <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.situation')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{fiscalSituationDigits(evidence.situation_code) || '—'}</dd></div>
      <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.rate')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(evidence.nominal_rate)}</dd></div>
      <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.baseReduction')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(evidence.base_reduction_rate ?? null)}</dd></div>
      <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.effectiveRate')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(evidence.effective_rate ?? null)}</dd></div>
      {evidence.evidence_role === 'prior_bookkeeping' && <>
        <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.creditRate')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(evidence.credit_rate)}</dd></div>
        <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.credit')}</dt><dd className="mt-0.5 font-semibold text-emerald-700 dark:text-emerald-300">{t(evidence.has_credit ? 'accounting.fiscalMemory.details.tax.withCredit' : 'accounting.fiscalMemory.details.tax.withoutCredit')}</dd></div>
      </>}
      {evidence.tax_code.toLowerCase() === 'ibs' && <>
        <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.stateRate')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(typeof evidence.attrs?.ibs_state_rate === 'number' ? evidence.attrs.ibs_state_rate : null)}</dd></div>
        <div><dt className="text-slate-500 dark:text-slate-400">{t('accounting.fiscalMemory.details.tax.municipalRate')}</dt><dd className="mt-0.5 font-semibold text-slate-800 dark:text-slate-200">{formatPercent(typeof evidence.attrs?.ibs_municipal_rate === 'number' ? evidence.attrs.ibs_municipal_rate : null)}</dd></div>
      </>}
    </dl>
  )

  const operationHeader = (group: MemoryOperationGroup) => {
    const operation = group.operation
    const taxReference = group.taxes[0]?.reference
    const xmlCfop = operation?.source_cfop || '—'
    const bookkeepingCfop = operation?.bookkeeping_cfop || '—'
    const nature = operation?.operation_nature || ''
    const partyName = operation?.party_name || taxReference?.party_name || '—'
    const partyCode = details.party_codes.find((code) => code.party_id && code.party_id === (operation?.party_id || taxReference?.party_id))
    const date = operation?.last_seen_at || taxReference?.last_seen_at || null
    return <>
      <header className="flex flex-col justify-between gap-3 bg-slate-50 p-4 sm:flex-row sm:items-start dark:bg-slate-900">
        <div className="min-w-0"><p className="font-semibold text-slate-900 dark:text-white">{nature || translatedOrValue(`accounting.fiscalMemory.details.operationTypes.${operation?.operation_type_key || 'unknown'}`, operation?.operation_type_key || null)}</p><p className="mt-1 truncate text-xs text-slate-500" title={operation?.source_document_name || taxReference?.source_document_name || undefined}>{partyName}{partyCode ? ` · ${t('accounting.fiscalMemory.details.partyCode')} ${partyCode.code}` : ''}</p></div>
        <div className="flex shrink-0 flex-wrap items-center gap-2"><span className="rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">{t('accounting.fiscalMemory.details.sourceCfop')} {xmlCfop}</span><span className="rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900">{t('accounting.fiscalMemory.details.bookkeepingCfop')} {bookkeepingCfop}</span><span className="rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">{formatDate(date)}</span></div>
      </header>
      <p className="px-4 pt-4 text-xs text-slate-500">{t('accounting.fiscalMemory.details.majorityPattern', { matched: String(Math.round(Number(operation?.occurrence_count || 0) * Number(operation?.confidence || 0))), total: String(operation?.occurrence_count || 0) })} · {Math.round(Number(operation?.confidence || 0) * 100)}%</p>
    </>
  }

  const renderOperationSections = (taxOrder?: readonly string[]) => {
    const groupsWithContent = taxOrder
      ? operationGroups.filter((group) => group.taxes.some((tax) => taxOrder.includes(tax.reference.tax_code.toLowerCase())))
      : operationGroups
    if (groupsWithContent.length === 0) {
      const emptyKey = taxOrder === TAX_REFORM_ORDER
        ? 'accounting.fiscalMemory.details.noTaxReformMemory'
        : taxOrder
          ? 'accounting.fiscalMemory.details.noTaxMemory'
          : 'accounting.fiscalMemory.details.noOperations'
      return <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">{t(emptyKey)}</p>
    }
    return (['incoming', 'outgoing'] as const).map((direction) => {
      const groups = groupsWithContent.filter((group) => group.direction === direction)
      if (groups.length === 0) return null
      return <section key={direction}>
        <div className="mb-3 flex items-center gap-2"><h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`accounting.fiscalMemory.details.operationSections.${direction}`)}</h3><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">{groups.length}</span></div>
        <div className="space-y-3">{groups.map((group) => {
          const orderedTaxes = taxOrder
            ? group.taxes
              .filter((tax) => taxOrder.includes(tax.reference.tax_code.toLowerCase()))
              .sort((left, right) => taxOrder.indexOf(left.reference.tax_code.toLowerCase()) - taxOrder.indexOf(right.reference.tax_code.toLowerCase()))
            : []
          return <article key={group.key} className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
            {operationHeader(group)}
            {taxOrder && <div className="p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('accounting.fiscalMemory.details.taxesForOperation')}</h4>
              <div className="mt-2 grid gap-3 lg:grid-cols-2">{orderedTaxes.map((taxGroup) => <section key={taxGroup.key} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"><div className="flex items-center justify-between gap-2"><p className="font-semibold uppercase text-slate-900 dark:text-white">{taxGroup.reference.tax_code}</p><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">{taxGroup.confidence}%</span></div><p className="mt-1 text-[11px] text-slate-500">{t('accounting.fiscalMemory.details.majorityPattern', { matched: String(taxGroup.occurrenceCount), total: String(taxGroup.totalOccurrences) })}</p><div className="mt-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-900"><div className="flex items-center justify-between gap-2"><p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{t(`accounting.fiscalMemory.details.roles.${taxGroup.reference.evidence_role}`)}</p><span className="truncate text-[11px] text-slate-500">{sourceLabel(taxGroup.reference.source_kind)}</span></div>{taxValues(taxGroup.reference)}</div></section>)}</div>
            </div>}
            {!taxOrder && <div className="h-4" />}
          </article>
        })}</div>
      </section>
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('accounting.fiscalMemory.details.title')}
      size="lg"
      closeLabel={t('actions.close')}
    >
      {product && <div className="flex flex-col gap-5 md:h-[72vh]">
        <header className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">{product.internal_code || '—'}</p>
            <h2 className="mt-1 truncate text-lg font-semibold text-slate-900 dark:text-white" title={product.name}>{product.name}</h2>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${confidenceClass}`}>
            {t(`accounting.fiscalMemory.confidence.${product.classification_status}`)} · {product.classification_confidence}%
          </span>
        </header>

        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700" aria-label={t('accounting.fiscalMemory.details.tabs.label')}>
          {(['summary', 'operations', 'taxes', 'taxReform'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === tab
                ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
            >
              {t(`accounting.fiscalMemory.details.tabs.${tab}`)}
              {tab === 'operations' && !loading && !error && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">{operationGroups.length}</span>}
              {tab === 'taxes' && !loading && !error && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">{details.tax_evidence.filter((tax) => CURRENT_TAX_ORDER.includes(tax.tax_code.toLowerCase() as typeof CURRENT_TAX_ORDER[number])).length}</span>}
              {tab === 'taxReform' && !loading && !error && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">{details.tax_evidence.filter((tax) => TAX_REFORM_ORDER.includes(tax.tax_code.toLowerCase() as typeof TAX_REFORM_ORDER[number])).length}</span>}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {feedback}
          {!feedback && activeTab === 'summary' && <div className="space-y-5">
            <dl className="grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4 dark:bg-slate-900">
              <div className="sm:col-span-2"><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.classification')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{purposeLabel(product.item_purpose_key)}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.itemType')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{product.sped_item_type_code || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.uom')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{product.uom || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.ncm')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{product.ncm || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.cest')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{product.cest || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.source')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{sourceLabel(product.source_kind)}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.period')}</dt><dd className="mt-1 whitespace-nowrap font-semibold text-slate-900 dark:text-white">{formatDate(product.source_period_start)} – {formatDate(product.source_period_end)}</dd></div>
            </dl>

            <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <article className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.confidence')}</p><p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{product.classification_confidence}%</p></article>
              <article className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.classificationEvidence')}</p><p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{product.classification_observations}</p></article>
              <article className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.operationEvidence')}</p><p className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">{product.operation_observations}</p></article>
              <article className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"><p className="text-xs text-slate-500">{t('accounting.fiscalMemory.details.stage')}</p><p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{t(`accounting.fiscalMemory.details.stages.${product.memory_stage}`)}</p></article>
            </section>

            {product.previous_codes.length > 0 && <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('accounting.fiscalMemory.details.previousCodes')}</p>
              <div className="mt-2 flex flex-wrap gap-2">{product.previous_codes.map((code) => <span key={code} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">{code}</span>)}</div>
            </section>}

            <p className={`rounded-xl p-4 text-sm ${product.automatic_eligible ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-950/20 dark:text-amber-200'}`}>
              {t(product.automatic_eligible ? 'accounting.fiscalMemory.details.automationEligible' : 'accounting.fiscalMemory.details.automationPending')}
            </p>
          </div>}

          {!feedback && activeTab === 'operations' && <div className="space-y-6">{renderOperationSections()}</div>}
          {!feedback && activeTab === 'taxes' && <div className="space-y-6">{renderOperationSections(CURRENT_TAX_ORDER)}</div>}
          {!feedback && activeTab === 'taxReform' && <div className="space-y-6">{renderOperationSections(TAX_REFORM_ORDER)}</div>}
        </div>
      </div>}
    </Modal>
  )
}
